import { useRef, useState } from 'react'
import {
  Button,
  Card,
  Col,
  Collapse,
  Container,
  Form,
  FormLabel,
  Row,
  Table
} from 'react-bootstrap'
import Breadcrumb from '../../../components/Label/Breadcrumb'
import { useLocation, useNavigate } from 'react-router-dom'
import api from '../../../config/AxiosInstance'
import * as Common from '../../../utils/Common'
import Loading from '../../../components/atoms/Loading'
import toast, { Toaster } from 'react-hot-toast'
import { Formik } from 'formik'
import { PurchaseOrderPackingCreateSchema } from '../../../utils/validationSchema'
import Select from 'react-select'
import {
  capitalizeWords,
  convertDateWithTime,
  transformKey
} from '../../../helpers/constants'
import { ChevronDown, ChevronUp, Check, X, Trash2, Edit } from 'lucide-react'
import DatePickerComponent from '../../../components/Input/DatePicker'
import ModalUpdateVehiclePrDraft from '../../../components/modal/pr/ModalUpdateVehiclePrDraft'
import Swal from 'sweetalert2'

const cardStyle = {
  border: 'none',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  borderRadius: '12px',
  padding: '1.5rem',
  marginBottom: '1.5rem',
  backgroundColor: '#fff'
}

const labelDetailStyle = {
  fontSize: '0.75rem',
  fontWeight: '700',
  color: '#999',
  textTransform: 'uppercase',
  marginBottom: '0.25rem',
  letterSpacing: '0.5px'
}

const tableHeaderStyle = {
  fontSize: '14px',
  fontWeight: '600',
  color: '#333333',
  letterSpacing: '0.5px',
  backgroundColor: '#f8f9fa',
  borderBottom: '2px solid #e9ecef'
}

const StyledTextField = ({
  label,
  value,
  name,
  handleChange,
  handleBlur,
  touched,
  errors,
  type = 'text',
  placeholder,
  required,
  disabled,
  as,
  col = 12
}) => {
  return (
    <Form.Group className={`mb-3 col-12 col-md-${col}`}>
      <Form.Label style={labelDetailStyle}>
        {label} {required && <sup className="text-danger">*</sup>}
      </Form.Label>
      <Form.Control
        as={as}
        type={type}
        name={name}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        isInvalid={touched?.[name] && !!errors?.[name]}
        placeholder={placeholder}
        disabled={disabled}
        className={`px-3`}
        style={{
          fontSize: '0.9rem',
          height: as === 'textarea' ? 'auto' : '40px',
          borderRadius: '8px',
          border: '1px solid #ced4da',
          backgroundColor: disabled ? '#e9ecef' : '#fff',
          cursor: disabled ? 'not-allowed' : 'default'
        }}
        autoComplete="off"
      />
      <Form.Control.Feedback type="invalid">
        {errors?.[name]}
      </Form.Control.Feedback>
    </Form.Group>
  )
}

function CreatePurchaseOrderPacking() {
  const customStyles = {
    control: (base) => ({
      ...base,
      height: 33,
      minHeight: 33
    }),
    valueContainer: (provided, state) => ({
      ...provided,
      height: '30px',
      padding: '0 6px'
    }),
    input: (provided, state) => ({
      ...provided,
      margin: '0px'
    })
  }
  const { state } = useLocation()
  const navigate = useNavigate()
  const refSeriesNumber = useRef()
  const refSrcWarehouses = useRef()
  const [loading, setLoading] = useState({
    page: false,
    submit: false
  })
  const [showModal, setShowModal] = useState(false)
  const [selectVehicle, setSelectVehicle] = useState(null)
  const [warehouses, setWarehouses] = useState([])
  const [itemsPO, setItemsPO] = useState([])
  const [data, setData] = useState({
    status: '',
    po_name: '',
    is_packing: false,
    principle: '',
    dest_warehouse: '',
    supplier: '',
    branch: '',
    acc_warehouse: '',
    type: '',
    po_date: '',
    po_category: '',
    items: [],
    prp_items: []
  })
  const [collapsible, setCollapsible] = useState([
    {
      label: 'arrival_info',
      open: true
    },
    {
      label: 'vehicle',
      open: true
    },
    {
      label: 'packing',
      open: true
    }
  ])
  const [inputData, setInputData] = useState({
    plate_no: '',
    items: {
      item_name: '',
      qty: null
    }
  })
  const [series, setSeries] = useState([])
  const fetchData = async () => {
    try {
      setLoading((prevLoading) => ({ ...prevLoading, page: true }))
      const resItem = await api.get(
        `inbound/pr-draft/show-items?po=${
          encodeURIComponent(state?.data?.name) || ''
        }`
      )
      let items = []
      const { data: dataItems } = resItem
      if (dataItems.success) {
        items = dataItems.data
        setItemsPO(dataItems.data)
      }
      const res = await api.get(
        `inbound/purchase-order/show?po=${
          encodeURIComponent(state?.data?.name) || ''
        }`
      )
      const { data } = res
      if (data.success) {
        setData({
          ...data.data,
          items: data?.data?.items.map((itemValue) => {
            return {
              ...itemValue,
              item_id: items.find((item) => item.item_name === itemValue.name)
                ?.id
            }
          })
        })
        fetchWarehouses(data?.data?.branch)
      }
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.message || 'An error occurred'
      Common.showPopUpErrorMessage(errMsg)
    } finally {
      setLoading((prevLoading) => ({ ...prevLoading, page: false }))
    }
  }
  const fetchSeries = async () => {
    try {
      const res = await api.get(
        `inbound/pr-draft/show-series?po=${
          encodeURIComponent(state?.data?.name) || ''
        }`
      )
      const { data } = res.data
      const series = data.map((item) => ({
        ...item,
        label: item.name,
        value: item.name
      }))
      setSeries(series)
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.message || 'An error occurred'
      Common.showPopUpErrorMessage(errMsg)
    }
  }
  const fetchWarehouses = async (branch) => {
    try {
      const params = {
        branch
      }
      const res = await api.get('/inbound/pr-draft/accepted-warehouses', {
        params
      })
      if (res.status === 200) {
        setWarehouses(res.data.data)
      }
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.message || 'An error occurred'
      Common.showPopUpErrorMessage(errMsg)
    }
  }
  const handleCollapse = (value) => {
    setCollapsible((prevState) =>
      prevState.map((item) => {
        if (item.label === value) {
          const open = !item.open
          return {
            ...item,
            open,
            icon: open ? <ChevronUp size={16} /> : <ChevronDown size={16} />
          }
        }
        return item
      })
    )
  }

  const handleInput = (target, value, targetSub = undefined) => {
    if (target === 'items') {
      setInputData((prevState) => ({
        ...prevState,
        items: {
          ...prevState.items,
          [targetSub]: value
        }
      }))
    } else {
      setInputData((prevState) => ({
        ...prevState,
        [target]: value
      }))
    }
  }
  const handleAddItem = (fieldChange, values) => {
    // checked item qty
    const itemExist = data?.items?.find(
      (item) => item.name === inputData.items.item_name
    )
    if (!itemExist) {
      toast.error('Item not found!', {
        position: 'top-right',
        style: {
          fontSize: '14px',
          fontWeight: '600'
        }
      })
      return
    }
    const itemQty = itemExist.qty.split(' ')
    if (Number(itemQty[0]) < Number(inputData.items.qty)) {
      toast.error('Item qty is not enough!', {
        position: 'top-right',
        style: {
          fontSize: '14px',
          fontWeight: '600'
        }
      })
      return
    }
    let dataItems = null
    if (!values.length) {
      const newItem = {
        id: 1,
        item_name: inputData.items.item_name,
        item_id: itemExist.item_id,
        total_pack: Number(inputData.items.qty),
        old_total_pack: Number(inputData.items.qty),
        isEdit: false
      }
      dataItems = [...values, newItem]
    } else {
      // check data already exist or not, when exist add qty based on item
      const itemChecked = values?.find(
        (item) => item.item_name === inputData.items.item_name
      )
      if (itemChecked) {
        dataItems = values.map((item) => {
          if (item.item_name === inputData.items.item_name) {
            const newTotalPack =
              Number(item.total_pack) + Number(inputData.items.qty)
            if (Number(itemQty[0]) < newTotalPack) {
              toast.error('Item qty is not enough!', {
                position: 'top-right',
                style: {
                  fontSize: '14px',
                  fontWeight: '600'
                }
              })
              return item
            }
            return {
              ...item,
              total_pack: newTotalPack
            }
          }
          return item
        })
      } else {
        dataItems = [
          ...values,
          {
            id: values.length + 1,
            item_name: inputData.items.item_name,
            item_id: itemExist.item_id,
            total_pack: Number(inputData.items.qty),
            old_total_pack: Number(inputData.items.qty),
            isEdit: false
          }
        ]
      }
    }
    fieldChange('packings', dataItems)
    // when success reset data property item
    setInputData((prevInput) => ({
      ...prevInput,
      items: {
        item_name: '',
        qty: ''
      }
    }))
  }
  const handleAddVehicle = (fieldChange, values) => {
    // checked values vehicle already exist
    const vehicleExist = values?.find(
      (item) => item.plate_no === inputData.plate_no
    )
    if (vehicleExist) {
      toast.error('Vehicle already exist!', {
        position: 'top-right',
        style: {
          fontSize: '14px',
          fontWeight: '600'
        }
      })
      return
    }
    const newVehicle = {
      id: values?.length + 1,
      plate_no: inputData.plate_no,
      sim_doc: null,
      sim_validity: null,
      sim_notes: null,
      stnk_doc: null,
      stnk_validity: null,
      stnk_notes: null
    }
    fieldChange('vehicles', [...values, newVehicle])
    // when success reset input data plate.no
    setInputData((prevInput) => ({
      ...prevInput,
      plate_no: ''
    }))
  }
  const renderDocumentVehicle = (valueVehicle, target) => {
    if (![null, undefined, ''].includes(valueVehicle[target])) {
      if (valueVehicle[target] instanceof File) {
        return <span className="text-primary">{valueVehicle[target].name}</span>
      }
      if (
        typeof valueVehicle[target] === 'boolean' &&
        ['sim_validity', 'stnk_validity'].includes(target)
      ) {
        return valueVehicle[target] === true ? 'VALID' : 'INVALID'
      }
      return valueVehicle[target]
    }
    return '-'
  }
  const updateVehicle = (fieldChange, value, values) => {
    const vehicleDatas = values?.map((dataVehicle) => {
      if (dataVehicle.id === value.id) {
        return { ...dataVehicle, ...value }
      }
      return dataVehicle
    })
    fieldChange('vehicles', vehicleDatas)
  }
  const handleSubmit = async (values) => {
    Swal.fire({
      title: 'Send to ERP?',
      text: 'This action can’t be repeated!',
      icon: 'warning',
      iconColor: '#828282',
      showCancelButton: true,
      confirmButtonColor: '#f89d1d',
      confirmButtonText: 'Send',
      customClass: {
        title: 'swal-title-outbound',
        popup: 'swal-popup-outbound',
        actions: 'swal-cancel-pr'
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setLoading((prevLoading) => ({ ...prevLoading, submit: true }))
          if (state?.type === 'import') {
            values.pib_date = convertDateWithTime(values.pib_date, 'YYYY-MM-DD')
          }
          const item_coas = Object.assign(
            {},
            ...(values.packings?.map((item, index) => ({
              [`item_coas[${index}][id]`]: item.id,
              [`item_coas[${index}][items][id]`]: item.item_id,
              [`item_coas[${index}][items][item_name]`]: item.item_name,
              [`item_coas[${index}][total_pack]`]: item.total_pack,
              [`item_coas[${index}][item_id]`]: item.item_id
            })) ?? [])
          )
          const { packings, ...dataValues } = values
          const payload = {
            ...dataValues,
            ...item_coas
          }
          const res = await api.postForm(`/inbound/pr-draft`, payload)
          const { data: dataRes } = res
          if (dataRes.success) {
            navigate(
              `/inbound/purchase-receipt/detail/packing/${dataRes?.data?.id}`
            )
            toast.success('PR Draft Created!', {
              position: 'top-right',
              style: {
                fontSize: '14px',
                fontWeight: '600'
              }
            })
          }
        } catch (error) {
          const errMsg =
            error?.response?.data?.errors?.message ||
            error?.message ||
            'An error occurred'
          toast.error(errMsg, {
            position: 'top-right',
            style: {
              fontSize: '14px',
              fontWeight: '600'
            }
          })
        } finally {
          setLoading((prevLoading) => ({ ...prevLoading, submit: false }))
        }
      }
    })
  }
  const columnVehicle = [
    {
      key: 'sequence',
      label: 'No'
    },
    {
      key: 'plate_no',
      label: 'Plate No'
    },
    {
      key: 'sim_doc',
      label: 'SIM',
      component: (data) => renderDocumentVehicle(data, 'sim_doc'),
      className: 'text-end'
    },
    {
      key: 'sim_validity',
      label: 'Validity',
      component: (data) => renderDocumentVehicle(data, 'sim_validity'),
      className: 'text-end'
    },
    {
      key: 'sim_notes',
      label: 'NOTES',
      component: (data) => renderDocumentVehicle(data, 'sim_notes'),
      className: 'text-end'
    },
    {
      key: 'stnk_doc',
      label: 'STNK',
      component: (data) => renderDocumentVehicle(data, 'stnk_doc'),
      className: 'text-end'
    },
    {
      key: 'stnk_validity',
      label: 'Validity',
      component: (data) => renderDocumentVehicle(data, 'stnk_validity'),
      className: 'text-end'
    },
    {
      key: 'stnk_notes',
      label: 'NOTES',
      component: (data) => renderDocumentVehicle(data, 'stnk_notes'),
      className: 'text-end'
    },
    {
      key: 'action',
      label: 'Action',
      className: 'text-end'
    }
  ]
  const columnPacking = [
    {
      key: 'sequence',
      label: 'No'
    },
    {
      key: 'item_name',
      label: 'Item'
    },
    {
      key: 'total_pack',
      label: 'Total Pack',
      className: 'text-end'
    },
    {
      key: 'action',
      label: 'Action',
      className: 'text-end'
    }
  ]
  useState(() => {
    if (!state?.data?.name) {
      toast.error('Purchase Order not found!', {
        position: 'top-right',
        style: {
          fontSize: '14px',
          fontWeight: '600'
        }
      })
      return navigate('/purchase-order')
    }
    fetchData()
    fetchSeries()
  }, [state?.data?.name])

  if (loading.page) {
    return <Loading />
  }
  return (
    <>
      <Toaster />
      <Container className="mb-5">
        <Row>
          <Col md={12} className="mt-3">
            <Breadcrumb
              label="Create PR Draft"
              link="/purchase-order"
              back="PURCHASE ORDER"
              status=""
            />
          </Col>
        </Row>
        <Row className="mt-3">
          <Formik
            initialValues={{
              po_num: data?.po_name || '',
              pr_type: state?.type || '',
              expedition: '',
              branch: data?.branch || '',
              supplier: data?.supplier || '',
              series_number: '',
              accepted_warehouse: '',
              pr_method: state?.methodValue || '',
              do: '',
              pib_date: '',
              pic_name: '',
              pic_number: '',
              aju: '',
              vehicles: [],
              packings: []
            }}
            enableReinitialize={true}
            validateOnChange={true}
            validationSchema={PurchaseOrderPackingCreateSchema}
            onSubmit={(values) => {
              handleSubmit(values)
            }}
          >
            {({
              handleSubmit,
              handleChange,
              handleBlur,
              values,
              touched,
              errors,
              isValid,
              dirty,
              setFieldValue,
              setFieldTouched
            }) => {
              return (
                <Form onSubmit={handleSubmit}>
                  <Row className="mb-4">
                    <Col md={12}>
                      <Card style={cardStyle}>
                        <Row>
                          <StyledTextField
                            label="#po"
                            name="po_name"
                            value={data?.po_name || ''}
                            touched={touched}
                            errors={errors}
                            disabled={true}
                            col={3}
                          />
                          <StyledTextField
                            label="po type"
                            name="po_type"
                            value={state?.type}
                            touched={touched}
                            errors={errors}
                            disabled={true}
                            col={3}
                          />
                          <StyledTextField
                            label="branch"
                            name="po_branch"
                            value={data?.branch || ''}
                            touched={touched}
                            errors={errors}
                            disabled={true}
                            col={3}
                          />
                          <StyledTextField
                            label="supplier"
                            name="po_supplier"
                            value={data?.supplier || ''}
                            touched={touched}
                            errors={errors}
                            disabled={true}
                            col={3}
                          />
                          <Col md={3} className="mb-3">
                            <FormLabel style={labelDetailStyle}>
                              Series Number
                            </FormLabel>
                            <Select
                              isClearable
                              defaultValue={values.series_number || ''}
                              styles={customStyles}
                              onChange={(e) => {
                                setFieldValue('series_number', e?.value)
                                setFieldTouched('series_number', true, false)
                              }}
                              options={series}
                              isSearchable
                              ref={refSeriesNumber}
                            />
                            {errors?.series_number &&
                              touched?.series_number && (
                                <div className="text-danger">
                                  {errors.series_number}
                                </div>
                              )}
                          </Col>
                          <Col md={3} className="mb-3">
                            <FormLabel style={labelDetailStyle}>
                              Accepted Warehouse
                            </FormLabel>
                            <Select
                              isClearable
                              defaultValue={values.accepted_warehouse || ''}
                              styles={customStyles}
                              onChange={(e) => {
                                if (!e) {
                                  e = {
                                    target: refSrcWarehouses,
                                    value: ''
                                  }
                                }
                                setFieldValue('accepted_warehouse', e?.value)
                                setFieldTouched(
                                  'accepted_warehouse',
                                  true,
                                  false
                                )
                              }}
                              options={warehouses.map((warehouse) => {
                                return {
                                  value: warehouse.id,
                                  label: warehouse.warehouse_id
                                }
                              })}
                              isSearchable
                              ref={refSrcWarehouses}
                            />
                            {touched?.['accepted_warehouse'] &&
                              !!errors?.['accepted_warehouse'] && (
                                <div className="text-danger">
                                  {errors?.['accepted_warehouse']}
                                </div>
                              )}
                          </Col>
                          <StyledTextField
                            label="method"
                            name="method"
                            value={
                              state?.methodValue
                                ? transformKey(state?.methodValue)
                                : ''
                            }
                            touched={touched}
                            errors={errors}
                            disabled={true}
                            col={3}
                          />
                        </Row>
                      </Card>
                    </Col>
                  </Row>
                  {/* ARRIVAL */}
                  <Card style={cardStyle}>
                    <Row className="mb-3">
                      <Col md={12} className="mb-2">
                        <div className="d-flex justify-content-between align-items-center">
                          <h5 className="text-secondary" style={{ margin: 0 }}>
                            ARRIVAL INFO
                          </h5>
                          {collapsible[0].open ? (
                            <ChevronUp
                              size={16}
                              onClick={() => handleCollapse('arrival_info')}
                              style={{ cursor: 'pointer' }}
                            />
                          ) : (
                            <ChevronDown
                              size={16}
                              onClick={() => handleCollapse('arrival_info')}
                              style={{ cursor: 'pointer' }}
                            />
                          )}
                        </div>
                      </Col>

                      <Collapse in={collapsible[0].open}>
                        <div id="example-collapse-text">
                          <Row className="mb-3">
                            <StyledTextField
                              label="#do"
                              name="do"
                              value={values.do}
                              handleChange={(e) => {
                                const capitalizedValue = capitalizeWords(
                                  e.target.value
                                )
                                setFieldValue('do', capitalizedValue)
                              }}
                              handleBlur={handleBlur}
                              touched={touched}
                              errors={errors}
                              placeholder="Input do"
                              col={3}
                              required
                            />
                            <StyledTextField
                              label="expedition"
                              name="expedition"
                              value={values.expedition}
                              handleChange={(e) => {
                                const capitalizedValue = capitalizeWords(
                                  e.target.value
                                )
                                setFieldValue('expedition', capitalizedValue)
                              }}
                              handleBlur={handleBlur}
                              touched={touched}
                              errors={errors}
                              placeholder="Input expedition"
                              col={3}
                              required
                            />
                            {state?.type === 'import' && (
                              <>
                                <DatePickerComponent
                                  label="pib date"
                                  name="pib_date"
                                  value={values.pib_date}
                                  handleChange={setFieldValue}
                                  handleBlur={() =>
                                    setFieldTouched('pib_date', true)
                                  }
                                  errors={errors}
                                  touched={touched}
                                  col={3}
                                  required
                                />
                                <StyledTextField
                                  label="#aju"
                                  name="aju"
                                  value={values.aju}
                                  handleChange={(e) => {
                                    const capitalizedValue = capitalizeWords(
                                      e.target.value
                                    )
                                    setFieldValue('aju', capitalizedValue)
                                  }}
                                  handleBlur={handleBlur}
                                  touched={touched}
                                  errors={errors}
                                  col={3}
                                  required
                                  placeholder="Input aju"
                                />
                              </>
                            )}
                            <StyledTextField
                              label="pic name"
                              name="pic_name"
                              value={values.pic_name}
                              handleChange={(e) => {
                                const capitalizedValue = capitalizeWords(
                                  e.target.value
                                )
                                setFieldValue('pic_name', capitalizedValue)
                              }}
                              handleBlur={handleBlur}
                              touched={touched}
                              errors={errors}
                              col={3}
                              required
                              placeholder="Input pic name"
                            />
                            <StyledTextField
                              label="pic number"
                              name="pic_number"
                              value={values.pic_number}
                              handleChange={handleChange}
                              handleBlur={handleBlur}
                              touched={touched}
                              errors={errors}
                              type="number"
                              col={3}
                              required
                              placeholder="Input pic number"
                            />
                          </Row>
                        </div>
                      </Collapse>
                    </Row>
                  </Card>
                  {/* VEHICLES*/}
                  <Card style={cardStyle}>
                    <Row className="mb-3">
                      <Col md={12} className="mb-2">
                        <div className="d-flex justify-content-between align-items-center">
                          <h5 className="text-secondary" style={{ margin: 0 }}>
                            VEHICLES
                          </h5>
                          {collapsible[1].open ? (
                            <ChevronUp
                              size={16}
                              onClick={() => handleCollapse('vehicle')}
                              style={{ cursor: 'pointer' }}
                            />
                          ) : (
                            <ChevronDown
                              size={16}
                              onClick={() => handleCollapse('vehicle')}
                              style={{ cursor: 'pointer' }}
                            />
                          )}
                        </div>
                      </Col>
                      <Collapse in={collapsible[1].open}>
                        <div id="example-collapse-text">
                          <Row className="mb-3">
                            <StyledTextField
                              label="Plate No"
                              name="plate_no"
                              value={inputData.plate_no}
                              handleChange={(e) => {
                                const capitalizedValue = capitalizeWords(
                                  e.target.value
                                )
                                handleInput('plate_no', capitalizedValue.trim())
                              }}
                              handleBlur={handleBlur}
                              touched={touched}
                              errors={errors}
                              placeholder="Input Plate No"
                              col={10}
                              required
                            />
                            <Col md={2}>
                              <div className="d-flex align-items-center h-100 mt-2">
                                <Button
                                  variant="transparent"
                                  name="vehicles"
                                  className={`btn-primary-blue ${
                                    !inputData.plate_no ||
                                    inputData.plate_no === ''
                                      ? 'opacity-50'
                                      : ''
                                  }`}
                                  disabled={
                                    !inputData.plate_no ||
                                    inputData.plate_no === ''
                                  }
                                  onClick={() => {
                                    handleAddVehicle(
                                      setFieldValue,
                                      values.vehicles
                                    )
                                    setFieldTouched('vehicles', true, false)
                                  }}
                                  onBlur={handleBlur}
                                >
                                  Add Vehicle
                                </Button>
                              </div>
                            </Col>
                          </Row>
                          <Row className="p-2 me-2 my-3">
                            <Table>
                              <thead className="table-secondary">
                                <tr>
                                  {columnVehicle.map((column) => (
                                    <th
                                      style={tableHeaderStyle}
                                      className={`align-middle p-2 ${column.className}`}
                                      key={column.key}
                                    >
                                      <span>{column.label}</span>
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {values.vehicles.length === 0 && (
                                  <tr>
                                    <td
                                      colSpan={columnVehicle.length}
                                      className="p-2 text-center"
                                    >
                                      No data
                                    </td>
                                  </tr>
                                )}
                                {values?.vehicles?.map(
                                  (vehicle, indexVehicle) => (
                                    <tr key={indexVehicle}>
                                      {columnVehicle.map((column) => (
                                        <td
                                          key={column.key}
                                          className={`align-middle p-2 ${column.className}`}
                                        >
                                          {column.key === 'sequence' &&
                                            indexVehicle + 1}
                                          {column.key === 'action' && (
                                            <div
                                              key={data.id}
                                              className="d-flex gap-2 justify-content-end"
                                            >
                                              <Button
                                                variant="transparent"
                                                className="btn-secondary"
                                                onClick={() => {
                                                  setShowModal(true)
                                                  setSelectVehicle(vehicle)
                                                }}
                                              >
                                                <Edit size={14} />
                                              </Button>
                                              <Button
                                                variant="transparent"
                                                className="btn-danger"
                                                onClick={() =>
                                                  setFieldValue(
                                                    'vehicles',
                                                    values?.vehicles?.filter(
                                                      (vehicleItem) =>
                                                        vehicleItem.id !==
                                                        vehicle.id
                                                    )
                                                  )
                                                }
                                              >
                                                <Trash2 size={14} />
                                              </Button>
                                            </div>
                                          )}
                                          {column.component &&
                                            column.component(vehicle)}
                                          {!column.component &&
                                            vehicle[column.key]}
                                        </td>
                                      ))}
                                    </tr>
                                  )
                                )}
                              </tbody>
                              {touched?.['vehicles'] &&
                                !!errors?.['vehicles'] &&
                                !values.vehicles.length && (
                                  <tfoot>
                                    <tr>
                                      <td colSpan={columnVehicle.length}>
                                        <p className="text-danger fw-bold">
                                          {errors['vehicles']}
                                        </p>
                                      </td>
                                    </tr>
                                  </tfoot>
                                )}
                            </Table>
                          </Row>
                        </div>
                      </Collapse>
                    </Row>
                  </Card>
                  {/* PACKING*/}
                  <Card style={cardStyle}>
                    <Row className="mb-3">
                      <Col md={12} className="mb-2">
                        <div className="d-flex justify-content-between align-items-center">
                          <h5 className="text-secondary" style={{ margin: 0 }}>
                            PACKING
                          </h5>
                          {collapsible[2].open ? (
                            <ChevronUp
                              size={16}
                              onClick={() => handleCollapse('packing')}
                              style={{ cursor: 'pointer' }}
                            />
                          ) : (
                            <ChevronDown
                              size={16}
                              onClick={() => handleCollapse('packing')}
                              style={{ cursor: 'pointer' }}
                            />
                          )}
                        </div>
                      </Col>
                      <Collapse in={collapsible[2].open}>
                        <div id="example-collapse-text">
                          <Row className="mb-3">
                            <Col md={7}>
                              <FormLabel style={labelDetailStyle}>
                                Items <sup className="text-danger">*</sup>
                              </FormLabel>
                              <Form.Select
                                onChange={(e) =>
                                  handleInput(
                                    'items',
                                    e.target.value ?? '',
                                    'item_name'
                                  )
                                }
                                value={inputData.items.item_name}
                                style={{
                                  fontSize: '0.9rem',
                                  height: '40px',
                                  borderRadius: '8px',
                                  border: '1px solid #ced4da'
                                }}
                              >
                                <option value="">Select Items</option>
                                {itemsPO?.map((item) => (
                                  <option
                                    key={item.item_name}
                                    value={item.item_name}
                                  >
                                    {item.item_name}
                                  </option>
                                ))}
                              </Form.Select>
                            </Col>

                            <StyledTextField
                              label="Total Pack"
                              name="qty"
                              type="number"
                              value={inputData.items.qty}
                              handleChange={(e) => {
                                handleInput(
                                  'items',
                                  e.target.value ?? '',
                                  'qty'
                                )
                              }}
                              handleBlur={handleBlur}
                              touched={touched}
                              errors={errors}
                              placeholder="Input qty"
                              col={3}
                              required
                            />

                            <Col md={2}>
                              <div className="d-flex align-items-center h-100 mt-2">
                                <Button
                                  variant="transparent"
                                  name="packings"
                                  className={`btn-primary-blue ${
                                    !inputData.items.item_name ||
                                    inputData.items.item_name === '' ||
                                    [0, '', null].includes(inputData.items.qty)
                                      ? 'opacity-50'
                                      : ''
                                  }`}
                                  onClick={() => {
                                    handleAddItem(
                                      setFieldValue,
                                      values.packings
                                    )
                                    setFieldTouched('packings', true, false)
                                  }}
                                  onBlur={handleBlur}
                                  disabled={
                                    !inputData.items.item_name ||
                                    inputData.items.item_name === '' ||
                                    [0, '', null].includes(inputData.items.qty)
                                  }
                                >
                                  Add Item
                                </Button>
                              </div>
                            </Col>
                          </Row>
                          <Row className="p-2 me-2 my-3">
                            <Table>
                              <thead className="table-secondary">
                                <tr>
                                  {columnPacking.map((column) => (
                                    <th
                                      style={tableHeaderStyle}
                                      className={`align-middle p-2 ${column.className}`}
                                      key={column.key}
                                    >
                                      <span>{column.label}</span>
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {values.packings.length === 0 && (
                                  <tr>
                                    <td colSpan={columnPacking.length}>
                                      No data
                                    </td>
                                  </tr>
                                )}
                                {values?.packings?.map(
                                  (packing, indexPacking) => (
                                    <tr key={indexPacking}>
                                      {columnPacking.map((column) => (
                                        <td
                                          key={column.key}
                                          className={`align-middle p-2 ${column.className}`}
                                        >
                                          {column.key === 'sequence' &&
                                            indexPacking + 1}
                                          {column.key === 'action' && (
                                            <div
                                              key={data.id}
                                              className="d-flex gap-2 justify-content-end"
                                            >
                                              {packing.isEdit && (
                                                <>
                                                  <Button
                                                    variant="transparent"
                                                    className="border"
                                                    style={{ height: '37px' }}
                                                    title="approve update"
                                                    onClick={() => {
                                                      const itemExist =
                                                        data?.items?.find(
                                                          (item) =>
                                                            item.name ===
                                                            packing.item_name
                                                        )
                                                      if (!itemExist) {
                                                        toast.error(
                                                          'Item not found!',
                                                          {
                                                            position:
                                                              'top-right',
                                                            style: {
                                                              fontSize: '14px',
                                                              fontWeight: '600'
                                                            }
                                                          }
                                                        )
                                                        return
                                                      }
                                                      const itemQty =
                                                        itemExist.qty.split(' ')
                                                      if (
                                                        Number(itemQty[0]) <
                                                        Number(
                                                          packing.total_pack
                                                        )
                                                      ) {
                                                        toast.error(
                                                          'Item qty is not enough!',
                                                          {
                                                            position:
                                                              'top-right',
                                                            style: {
                                                              fontSize: '14px',
                                                              fontWeight: '600'
                                                            }
                                                          }
                                                        )
                                                        return
                                                      }
                                                      setFieldValue(
                                                        'packings',
                                                        values?.packings?.map(
                                                          (packingItem) => {
                                                            if (
                                                              packingItem.id ===
                                                              packing.id
                                                            ) {
                                                              return {
                                                                ...packingItem,
                                                                isEdit: false,
                                                                old_total_pack:
                                                                  packingItem.total_pack
                                                              }
                                                            }
                                                            return packingItem
                                                          }
                                                        )
                                                      )
                                                    }}
                                                  >
                                                    <Check
                                                      size={14}
                                                      color="green"
                                                    />
                                                  </Button>
                                                  <Button
                                                    variant="transparent"
                                                    className="border"
                                                    style={{ height: '37px' }}
                                                    title="cancel update"
                                                    onClick={() =>
                                                      setFieldValue(
                                                        'packings',
                                                        values?.packings?.map(
                                                          (packingItem) => {
                                                            if (
                                                              packingItem.id ===
                                                              packing.id
                                                            ) {
                                                              return {
                                                                ...packingItem,
                                                                isEdit: false,
                                                                total_pack:
                                                                  packingItem.old_total_pack
                                                              }
                                                            }
                                                            return packingItem
                                                          }
                                                        )
                                                      )
                                                    }
                                                  >
                                                    <X size={14} color="red" />
                                                  </Button>
                                                </>
                                              )}
                                              {!packing.isEdit && (
                                                <>
                                                  <Button
                                                    variant="transparent"
                                                    className="btn-secondary"
                                                    onClick={() =>
                                                      setFieldValue(
                                                        'packings',
                                                        values?.packings?.map(
                                                          (packingItem) => {
                                                            if (
                                                              packingItem.id ===
                                                              packing.id
                                                            ) {
                                                              return {
                                                                ...packingItem,
                                                                isEdit: true
                                                              }
                                                            }
                                                            return packingItem
                                                          }
                                                        )
                                                      )
                                                    }
                                                  >
                                                    <Edit size={14} />
                                                  </Button>
                                                  <Button
                                                    variant="transparent"
                                                    className="btn-danger"
                                                    onClick={() =>
                                                      setFieldValue(
                                                        'packings',
                                                        values.packings.filter(
                                                          (packingItem) =>
                                                            packingItem.id !==
                                                            packing.id
                                                        )
                                                      )
                                                    }
                                                  >
                                                    <Trash2 size={14} />
                                                  </Button>
                                                </>
                                              )}
                                            </div>
                                          )}
                                          {column.key === 'total_pack' &&
                                            packing.isEdit && (
                                              <Form.Control
                                                key={`input_total_pack_${packing.id}`}
                                                type="number"
                                                placeholder="Input"
                                                value={packing.total_pack}
                                                className="w-50"
                                                onChange={(e) => {
                                                  const { value: valueInput } =
                                                    e.target
                                                  setFieldValue(
                                                    'packings',
                                                    values?.packings?.map(
                                                      (packingValue) => {
                                                        if (
                                                          packingValue.id ===
                                                          packing.id
                                                        ) {
                                                          return {
                                                            ...packingValue,
                                                            total_pack:
                                                              valueInput
                                                          }
                                                        }
                                                        return packingValue
                                                      }
                                                    )
                                                  )
                                                }}
                                              />
                                            )}
                                          {column.key === 'total_pack' &&
                                            !packing.isEdit && (
                                              <span
                                                key={`total_pack_${packing.id}`}
                                              >
                                                {packing.total_pack}
                                              </span>
                                            )}
                                          {column.component &&
                                            column.component(packing)}
                                          {!column.component &&
                                            ![
                                              'sequence',
                                              'action',
                                              'total_pack'
                                            ].includes(column.key) &&
                                            packing[column.key]}
                                        </td>
                                      ))}
                                    </tr>
                                  )
                                )}
                              </tbody>
                              {touched?.['packings'] &&
                                !!errors?.['packings'] &&
                                !values.packings.length && (
                                  <tfoot>
                                    <tr>
                                      <td colSpan={columnPacking.length}>
                                        <p className="text-danger fw-bold">
                                          {errors['packings']}
                                        </p>
                                      </td>
                                    </tr>
                                  </tfoot>
                                )}
                            </Table>
                          </Row>
                        </div>
                      </Collapse>
                    </Row>
                  </Card>
                  <Row>
                    <Col md={12}>
                      <div className="d-flex justify-content-end">
                        <Button
                          variant="transparent"
                          className={`${
                            !dirty || !isValid || loading.submit
                              ? 'btn-secondary opacity-50'
                              : 'btn-primary-orange'
                          }`}
                          disabled={!dirty || !isValid || loading.submit}
                          style={{ height: '37px' }}
                          onClick={handleSubmit}
                        >
                          {loading.submit
                            ? 'Sending...'
                            : 'Submit And Send to ERP'}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                  <ModalUpdateVehiclePrDraft
                    show={showModal}
                    onHide={() => {
                      setShowModal(false)
                      setSelectVehicle(null)
                    }}
                    selectedVehicle={selectVehicle}
                    updateVehicleInList={updateVehicle}
                    isPacking
                    setFieldValue={setFieldValue}
                    dataValues={values?.vehicles || []}
                  />
                </Form>
              )
            }}
          </Formik>
        </Row>
      </Container>
    </>
  )
}

export default CreatePurchaseOrderPacking
